/**
 * Conditional Frontend Logger
 *
 * Wraps `console.*` calls with environment-aware gating.
 * - In development: all levels are emitted.
 * - In production: only `warn` and `error` are emitted; `log` and `debug` are no-ops.
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.log('info message');        // dev only
 *   logger.debug('debug detail');      // dev only
 *   logger.warn('something iffy');     // always
 *   logger.error('failure', err);      // always
 *   logger.group('section');           // dev only
 *   logger.groupEnd();                 // dev only
 *
 * Scoped loggers:
 *   const log = logger.scope('ChatService');
 *   log.log('connected');   // → [ChatService] connected
 */

const IS_DEV =
  typeof import.meta !== 'undefined' &&
  (import.meta as any).env?.DEV === true;

/* eslint-disable no-console */

const noop = (..._args: unknown[]): void => {};

export interface Logger {
  log: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  group: (...args: unknown[]) => void;
  groupEnd: () => void;
  scope: (name: string) => Logger;
}

function createLogger(prefix?: string): Logger {
  const tag = prefix ? `[${prefix}]` : '';

  const prefixed = (fn: (...a: unknown[]) => void) =>
    tag ? (...args: unknown[]) => fn(tag, ...args) : fn;

  return {
    log: IS_DEV ? prefixed(console.log) : noop,
    debug: IS_DEV ? prefixed(console.debug) : noop,
    warn: prefixed(console.warn),
    error: prefixed(console.error),
    group: IS_DEV ? prefixed(console.group) : noop,
    groupEnd: IS_DEV ? console.groupEnd.bind(console) : noop,
    scope: (name: string) => createLogger(prefix ? `${prefix}:${name}` : name),
  };
}

/** Global frontend logger — gates log/debug to dev-only. */
export const logger: Logger = createLogger();

export default logger;
